import React, { useEffect, useState, useMemo, useContext, useRef, useCallback } from 'react';
import {
    Body1,
    Button,
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Dropdown,
    Image,
    Input,
    Option,
    Spinner,
    Switch,
    Text,
    Title3,
    makeStyles,
    mergeClasses,
} from '@fluentui/react-components';
import {
    SettingsRegular,
    AddRegular,
    InfoRegular,
    SendRegular,
    BugRegular,
    DatabaseRegular,
    ChevronDownRegular,
    ChevronUpRegular,
} from '@fluentui/react-icons';
import { useHasDebugAccess } from '../utils/UserContext';
import { useDispatch, useSelector } from 'react-redux';
import { useTokenService } from '../tokenService';
import { CopilotService } from '../Service/ServerAPI';
import {
    setScriptMessage,
    setPseudoMessage,
    setSessionId,
    setProcessing,
    RootState,
    setActiveSessionId,
    setSessionDataset,
    setDataset,
    setAnalyticsMode,
    setSessionMode,
    selectEffectiveAnalytics,
    changeSetting,
    setIsViewingOthersSession
} from '../utils/store';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '../Logo.png';

interface DrawerContentProps {
    onSettingsClick: () => void;
    onAboutClick: () => void;
    onDebugClick: () => void;
    windowWidth: number;
    isAuthed: boolean;
    setShowScreen: (showScreen: boolean) => void;
}

const useStyles = makeStyles({
    content: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '100vh',
        background: '#25282c',
        color: 'white',
        position: 'relative',
        padding: '32px 24px',
        width: '345px',
        overflowY: 'auto',
        boxSizing: 'border-box',
        '::-webkit-scrollbar': {
            width: '6px',
        },
        '::-webkit-scrollbar-track': {
            background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '3px',
        },
        gap: '13px',
    },
    headerTop: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '17px',
        width: '100%',
    },
    newChatContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '25px 0',
        width: '100%',
    },
    newChatButton: {
        width: '100%',
        opacity: 0.9,
        border: '1px solid green',
    },
    scrollContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        overflowY: 'auto',
        width: 'calc(100% + 48px)',
        marginLeft: '-24px',
        marginRight: '-24px',
        paddingLeft: '24px',
        paddingRight: '24px',
        boxSizing: 'border-box',
        color: 'white',
        flex: 1,
        position: 'relative',
        '::-webkit-scrollbar': {
            width: '6px',
            background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
            background: '#3f3f3f86',
            borderRadius: '16px',
            opacity: 0,
            transition: 'opacity 0.3s ease',
        },
        '&:hover::-webkit-scrollbar-thumb': {
            opacity: 1,
        },
    },
    scrollVisible: {
        '::-webkit-scrollbar-thumb': {
            opacity: 1,
        },
    },
    sectionHeader: {
        color: '#ffffffc9',
        fontSize: '12px',
        fontWeight: 600,
        padding: '16px 8px 4px 8px',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.5px',
        flexShrink: 0,
    },
    chatButton: {
        padding: '8px 12px',
        justifyContent: 'flex-start',
        width: '100%',
        overflow: 'hidden',
        borderRadius: '8px',
        minHeight: '36px',
        flexShrink: 0,
    },
    chatButtonText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        width: '100%',
    },
    betaPill: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        fontSize: '11px',
        fontWeight: 600,
        borderRadius: '12px',
        backgroundColor: '#5dbb27',
        color: 'white',
        letterSpacing: '0.5px',
        textTransform: 'uppercase' as const,
        lineHeight: '18px',
        flexShrink: 0,
        margin: '0 0 0 7px',
        verticalAlign: 'text-bottom',
    },
    showMoreButton: {
        padding: '4px 12px',
        justifyContent: 'flex-start',
        width: '100%',
        minHeight: '32px',
        flexShrink: 0,
        color: '#ffffffa0',
        fontSize: '12px',
    },
    footer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '8px',
        width: '100%',
        color: 'white',
        flexShrink: 0,
        paddingTop: '8px',
        marginTop: 'auto',
    },
    datasetToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        padding: '8px 0',
        borderTop: '1px solid #3A3E45',
    },
    datasetDropdown: {
        flex: 1,
        minWidth: 0,
    },
    endpointPill: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 8px',
        fontSize: '11px',
        fontWeight: 600,
        borderRadius: '12px',
        backgroundColor: '#3f69dd',
        color: 'white',
        letterSpacing: '0.5px',
        textTransform: 'uppercase' as const,
        lineHeight: '18px',
        flexShrink: 0,
        marginLeft: '6px',
        verticalAlign: 'text-bottom',
        top: '2px',
    },
});

const DrawerContent: React.FC<DrawerContentProps> = ({
    onSettingsClick,
    onAboutClick,
    onDebugClick,
    isAuthed,
    setShowScreen
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const getAccessToken = useTokenService();
    const hasDebugAccess = useHasDebugAccess();
    const settings = useSelector((state: RootState) => state.settings);
    const hasAnalyticsAccess = settings.availableDatasets?.some(d => d.analyticsEnabled) ?? false;
    const activeSessionId = useSelector(
        (state: RootState) => state.settings.activeSessionId
    );
    const [loaded, setLoaded] = useState(false);
    const [customSessionId, setCustomSessionId] = useState('');
    const [isScrolling, setIsScrolling] = useState(false);
    const [modeDialogOpen, setModeDialogOpen] = useState(false);
    const [pendingMode, setPendingMode] = useState(false);
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(() => {
        setIsScrolling(true);
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
            setIsScrolling(false);
        }, 1000);
    }, []);

    const [sessionNames, setSessionNames] = useState<{
        [key: string]: { name: string; lastModified: number };
    }>({});
    const [allSessionIds, setAllSessionIds] = useState<string[]>([]);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

    const copilotService = CopilotService.getInstance(getAccessToken);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('sessionId');
        if (sessionId) {
            dispatch(setSessionId(sessionId));
            dispatch(setActiveSessionId(sessionId));
        }
        //fetchUserSessions();
    }, [dispatch, location.search]);

    // Effect to fetch user sessions on initial load
    useEffect(() => {
        fetchUserSessions(); // Call the function on component mount
    }, []);

    const handleNewChatClick = () => {
        dispatch(setScriptMessage([]));
        dispatch(setProcessing(false));
        dispatch(setPseudoMessage([]));
        dispatch(setSessionId(''));
        dispatch(setActiveSessionId(''));
        dispatch(setSessionDataset(''));
        dispatch(setSessionMode(''));
        dispatch(setIsViewingOthersSession(false));
        setShowScreen(true);
        dispatch(changeSetting({ key: 'useChatv2', value: true }));
    };

    // Analytics Mode toggle: a session can't switch modes mid-conversation, so confirm
    // and start a fresh chat in the requested mode (only prompt when a chat is in progress).
    const requestModeChange = (targetAnalytics: boolean) => {
        if (settings.chat.length > 0) {
            setPendingMode(targetAnalytics);
            setModeDialogOpen(true);
        } else {
            dispatch(setAnalyticsMode(targetAnalytics));
        }
    };

    const confirmModeChange = () => {
        handleNewChatClick();
        dispatch(setAnalyticsMode(pendingMode));
        setModeDialogOpen(false);
    };

    const handleChatButtonClick = (session: string) => {
        dispatch(setSessionId(session));
        dispatch(setActiveSessionId(session));
        navigate(`/?sessionId=${session}`);
        window.location.reload();
    };

    const formatChatName = (chatName: string, segments: number = 2): string => {
        const parts = chatName.split('-');
        return parts.slice(0, segments).join('-');
    };

    const now = Math.floor(Date.now() / 1000);
    const sevenDaysAgo = now - 7 * 24 * 60 * 60;

    // Group sessions into time buckets like ChatGPT
    const groupedSessions = useMemo(() => {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayEpoch = Math.floor(todayStart.getTime() / 1000);

        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(yesterdayStart.getDate() - 1);
        const yesterdayEpoch = Math.floor(yesterdayStart.getTime() / 1000);

        const thirtyDaysAgo = now - 30 * 24 * 60 * 60;

        const groups: { label: string; sessions: string[] }[] = [];
        const buckets: Record<string, string[]> = {};

        for (const sessionId of allSessionIds) {
            const ts = sessionNames[sessionId]?.lastModified ?? 0;
            let label: string;
            if (ts >= todayEpoch) {
                label = 'Today';
            } else if (ts >= yesterdayEpoch) {
                label = 'Yesterday';
            } else if (ts >= sevenDaysAgo) {
                label = 'Previous 7 Days';
            } else if (ts >= thirtyDaysAgo) {
                label = 'Previous 30 Days';
            } else {
                const date = new Date(ts * 1000);
                const monthNames = [
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December',
                ];
                label = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
            }

            if (!buckets[label]) {
                buckets[label] = [];
                groups.push({ label, sessions: buckets[label] });
            }
            buckets[label].push(sessionId);
        }

        return groups;
    }, [allSessionIds, sessionNames, now, sevenDaysAgo]);

    const fetchUserSessions = async () => {
        const response = await copilotService.getUserSessions(
            settings.apiEndpoint
        );
        if (response) {
            const names: {
                [key: string]: { name: string; lastModified: number };
            } = {};
            const sessionIds: string[] = [];
            for (const session of response.sessions) {
                try {
                    names[session.sessionId] = {
                        name:
                            session.sessionName ||
                            `Recent Chat: ${formatChatName(
                                session.sessionId,
                                1
                            )}`,
                        lastModified: session.lastModified,
                    };
                    sessionIds.push(session.sessionId);
                } catch (err) {
                    console.log('No Chat Found: ', session.sessionId);
                }
            }
            setSessionNames(names);
            setAllSessionIds(sessionIds);
            setLoaded(true);
        }
    };

    const handleCustomSessionIdChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setCustomSessionId(event.target.value);
    };

    const handleCustomSessionIdSubmit = () => {
        if (customSessionId) {
            navigate(`/?sessionId=${customSessionId}`);
            window.location.reload();
        }
    };

    return (
        <div className={classes.content}>
            <div className={classes.headerTop}>
                <Image
                    style={{ margin: '0px 8px 0 15px' }}
                    width={38}
                    height="auto"
                    alt="logo"
                    src={Logo}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div>
                        <Title3>
                            Gaming<br/>
                            Segmentation<br/>
                            Copilot
                        </Title3>
                        <span className={classes.betaPill}>Beta</span>
                    </div>
                </div>
            </div>
            {isAuthed && <div className={classes.newChatContainer}>
                <Button
                    appearance="primary"
                    aria-label="new-chat"
                    icon={<AddRegular />}
                    onClick={handleNewChatClick}
                    className={classes.newChatButton}
                >
                    NEW CHAT
                </Button>
            </div>}
            {isAuthed && <div className={mergeClasses(classes.scrollContainer, isScrolling && classes.scrollVisible)}
                ref={scrollContainerRef}
                onScroll={handleScroll}
            >
                {!loaded ? (
                    <Spinner size="large" style={{ paddingTop: '48%' }} />
                ) : (
                    groupedSessions.map((group) => {
                        const isExpanded = expandedSections.has(group.label);
                        const maxVisible = 5;
                        const visibleSessions = isExpanded
                            ? group.sessions
                            : group.sessions.slice(0, maxVisible);
                        const hasMore = group.sessions.length > maxVisible;

                        return (
                        <React.Fragment key={group.label}>
                            <div className={classes.sectionHeader}>
                                {group.label}
                            </div>
                            {visibleSessions.map((sessionId, index) => (
                                <Button
                                    appearance={
                                        sessionId === activeSessionId
                                            ? 'outline'
                                            : 'subtle'
                                    }
                                    className={classes.chatButton}
                                    aria-label={`chat-${group.label}-${index + 1}`}
                                    key={sessionId}
                                    onClick={() =>
                                        handleChatButtonClick(sessionId)
                                    }
                                >
                                    <Body1 className={classes.chatButtonText}>
                                        {sessionNames[sessionId].name}
                                    </Body1>
                                </Button>
                            ))}
                            {hasMore && (
                                <Button
                                    appearance="subtle"
                                    className={classes.showMoreButton}
                                    icon={isExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
                                    onClick={() => {
                                        setExpandedSections((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(group.label)) {
                                                next.delete(group.label);
                                            } else {
                                                next.add(group.label);
                                            }
                                            return next;
                                        });
                                    }}
                                >
                                    {isExpanded
                                        ? 'Show less'
                                        : `Show ${group.sessions.length - maxVisible} more`}
                                </Button>
                            )}
                            <hr style={{ borderColor: '#888888' }}/>
                        </React.Fragment>
                    );
                    })
                )}
            </div>
            }
            
            {isAuthed && <div className={classes.footer}>
                {settings.availableDatasets.length > 1 && (
                    <div className={classes.datasetToggle}>
                        <DatabaseRegular />
                        <Dropdown
                            className={classes.datasetDropdown}
                            value={settings.availableDatasets.find(d => d.name === settings.dataset)?.displayName || settings.dataset}
                            selectedOptions={[settings.dataset]}
                            onOptionSelect={(_, data) => {
                                if (data.optionValue && data.optionValue !== settings.dataset) {
                                    const hasActiveSession = settings.sessionId && settings.chat.length > 0;
                                    if (hasActiveSession) {
                                        const confirmed = window.confirm(
                                            'Changing datasets will start a new session. Your current session is saved and you can return to it later.\n\nContinue?'
                                        );
                                        if (!confirmed) return;
                                        // Start fresh
                                        dispatch(setScriptMessage([]));
                                        dispatch(setProcessing(false));
                                        dispatch(setPseudoMessage([]));
                                        dispatch(setSessionId(''));
                                        dispatch(setActiveSessionId(''));
                                        dispatch(setSessionDataset(''));
                                        dispatch(setIsViewingOthersSession(false));
                                        setShowScreen(true);
                                    }
                                    dispatch(setDataset(data.optionValue));
                                }
                            }}
                        >
                            {settings.availableDatasets.map((ds) => (
                                <Option key={ds.name} value={ds.name} disabled={ds.accessible === false}>
                                    {ds.accessible === false ? `${ds.displayName} (Restricted)` : ds.displayName}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>
                )}
                {hasAnalyticsAccess && (
                <div className={classes.datasetToggle}>
                    <Switch
                        checked={selectEffectiveAnalytics(settings)}
                        onChange={(_, data) => requestModeChange(data.checked)}
                        label="Analytics Mode"
                    />
                </div>
                )}
                <Dialog open={modeDialogOpen} onOpenChange={(_, data) => setModeDialogOpen(data.open)}>
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>Switch to {pendingMode ? 'Analytics' : 'Segmentation'} mode?</DialogTitle>
                            <DialogContent>
                                Switching modes starts a new chat. Your current conversation will be cleared.
                            </DialogContent>
                            <DialogActions>
                                <Button appearance="secondary" onClick={() => setModeDialogOpen(false)}>
                                    Cancel
                                </Button>
                                <Button appearance="primary" onClick={confirmModeChange}>
                                    Start new chat
                                </Button>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
                <Button
                    appearance="subtle"
                    aria-label="settings"
                    style={{ width: '100%', justifyContent: 'flex-start' }}
                    onClick={onSettingsClick}
                    icon={<SettingsRegular />}
                >
                    <Body1>Settings</Body1>
                    {settings.apiEndpoint !== 'https://wa-orchestration.azurewebsites.net' && (
                        <span className={classes.endpointPill}>
                            {settings.apiEndpoint === 'https://wa-orchestration-staging.azurewebsites.net'
                                ? 'Staging'
                                : settings.apiEndpoint === 'https://localhost:7147'
                                ? 'Local'
                                : 'Custom'}
                        </span>
                    )}
                </Button>
                <Button
                    appearance="subtle"
                    aria-label="about"
                    style={{ width: '100%', justifyContent: 'flex-start' }}
                    onClick={onAboutClick}
                    icon={<InfoRegular />}
                >
                    <Body1>About</Body1>
                </Button>
                {hasDebugAccess && (
                    <Button
                        appearance="subtle"
                        aria-label="debug"
                        style={{ width: '100%', justifyContent: 'flex-start' }}
                        onClick={onDebugClick}
                        icon={<BugRegular />}
                    >
                        <Body1>Debug Tools</Body1>
                    </Button>
                )}
                <Input
                    style={{ width: '95%', margin: '0 auto' }}
                    placeholder="Load Custom Session ID"
                    value={customSessionId}
                    onSubmit={handleCustomSessionIdSubmit}
                    onChange={handleCustomSessionIdChange}
                    contentAfter={
                        <Button
                            onClick={handleCustomSessionIdSubmit}
                            appearance="transparent"
                            icon={<SendRegular />}
                            aria-label="Load Session ID"
                        />
                    }
                />
            </div>
}
        </div>
    );
};

export default DrawerContent;
